import { useEffect, useState } from "react";

/**
 * Golden Thread — the visitor's journey rendered as a slender luminous line
 * down the left edge, growing with scroll. Every ~25% of the journey it
 * blooms briefly into a small vesica / hex geometry before resolving back
 * into a single flowing path. Symbolic of continuous growth.
 *
 * Sits below content but above the field. Pointer-events: none.
 */
export function GoldenThread() {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let raf = 0;
    const compute = () => {
      const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      const p = Math.min(1, Math.max(0, window.scrollY / max));
      setProgress(p);
      raf = 0;
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Branch nodes — quarter marks. Each expands as progress passes through it.
  const nodes = [0.25, 0.5, 0.75, 1.0];

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-y-0 left-3 z-30 hidden w-6 sm:block"
    >
      <svg
        viewBox="0 0 24 100"
        preserveAspectRatio="none"
        className="h-full w-full"
      >
        <defs>
          <linearGradient id="gt-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(244, 214, 117, 0.85)" />
            <stop offset="60%" stopColor="rgba(214, 168, 62, 0.55)" />
            <stop offset="100%" stopColor="rgba(214, 168, 62, 0)" />
          </linearGradient>
          <filter id="gt-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="0.6" />
          </filter>
        </defs>

        {/* Rail — dim background line */}
        <line x1="12" y1="0" x2="12" y2="100" stroke="rgba(214,168,62,0.10)" strokeWidth="0.4" />

        {/* Progress thread */}
        <line
          x1="12"
          y1="0"
          x2="12"
          y2={progress * 100}
          stroke="url(#gt-grad)"
          strokeWidth="0.8"
          strokeLinecap="round"
          filter="url(#gt-glow)"
        />

        {/* Node blooms — subtle vesica pattern near each quarter */}
        {nodes.map((n) => {
          const dist = Math.abs(progress - n);
          const active = dist < 0.06; // active band ~12% wide
          const strength = active ? 1 - dist / 0.06 : 0;
          if (strength <= 0) {
            return (
              <circle
                key={n}
                cx="12"
                cy={n * 100}
                r="0.6"
                fill="rgba(214,168,62,0.35)"
              />
            );
          }
          const r1 = 1.4 + strength * 2.2;
          const r2 = 0.8 + strength * 1.4;
          const alpha = 0.15 + strength * 0.5;
          return (
            <g key={n} transform={`translate(12 ${n * 100})`} opacity={0.6 + strength * 0.4}>
              {/* Vesica: two overlapping circles */}
              <circle cx={-r2 * 0.55} cy="0" r={r1} fill="none" stroke={`rgba(255,232,168,${alpha})`} strokeWidth="0.25" />
              <circle cx={r2 * 0.55} cy="0" r={r1} fill="none" stroke={`rgba(255,232,168,${alpha})`} strokeWidth="0.25" />
              {/* Six-pointed accent */}
              {[0, 60, 120].map((deg) => (
                <line
                  key={deg}
                  x1={-r1 * 0.6 * Math.cos((deg * Math.PI) / 180)}
                  y1={-r1 * 0.6 * Math.sin((deg * Math.PI) / 180)}
                  x2={r1 * 0.6 * Math.cos((deg * Math.PI) / 180)}
                  y2={r1 * 0.6 * Math.sin((deg * Math.PI) / 180)}
                  stroke={`rgba(255,236,180,${alpha * 0.9})`}
                  strokeWidth="0.15"
                />
              ))}
              <circle cx="0" cy="0" r={0.9 + strength * 0.4} fill="rgba(255,240,190,0.9)" />
            </g>
          );
        })}
      </svg>
    </div>
  );
}
